import {
  ActionIcon,
  Anchor,
  Badge,
  Button,
  Center,
  Group,
  Image,
  Pagination,
  Stack,
  Table,
  Text,
  Tooltip,
} from "@mantine/core";
import { IconTrash, IconExternalLink } from "@tabler/icons-react";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import type { HistoryEntry } from "@models/translation";
import { languageLabel } from "@config/languages";
import { usePagination } from "@hooks/usePagination";
import { useJobCost, formatUsd } from "@hooks/translation/useJobCost";
import emptyState from "@assets/icons/empty-state.svg";

const PAGE_SIZE = 10;

/** Per-row cost cell; fetches the job's total cost (USD) lazily. */
const CostCell: React.FC<{ jobId: string }> = ({ jobId }) => {
  const { t } = useTranslation();
  const { totalCostUsd, totalTokens } = useJobCost(jobId);
  if (totalCostUsd == null) {
    return (
      <Text size="xs" c="dimmed">
        —
      </Text>
    );
  }
  return (
    <Tooltip label={t("translation.costTokens", { tokens: totalTokens.toLocaleString() })}>
      <Text size="xs" fw={600}>
        {formatUsd(totalCostUsd)}
      </Text>
    </Tooltip>
  );
};

interface Props {
  entries: HistoryEntry[];
  onOpen: (entry: HistoryEntry) => void;
  onRemove: (id: string) => void;
  onClear: () => void;
}

const TargetBadges: React.FC<{ langs: string[] }> = ({ langs }) => {
  const shown = langs.slice(0, 4);
  const rest = langs.length - shown.length;
  return (
    <Group gap={4} wrap="nowrap">
      {shown.map((l) => (
        <Badge key={l} size="xs" variant="light" color="accent" style={{ maxWidth: "none" }}>
          {languageLabel(l)}
        </Badge>
      ))}
      {rest > 0 && (
        <Badge size="xs" variant="outline" color="gray">
          +{rest}
        </Badge>
      )}
    </Group>
  );
};

export const HistoryPanel: React.FC<Props> = ({ entries, onOpen, onRemove, onClear }) => {
  const { t } = useTranslation();
  const { page, setPage, pageItems, totalPages } = usePagination(entries, PAGE_SIZE);

  if (entries.length === 0) {
    return (
      <Center py={40}>
        <Stack align="center" gap="xs">
          <Image src={emptyState} w={100} opacity={0.5} alt="" />
          <Text c="dimmed" size="sm">{t("translation.history.empty")}</Text>
        </Stack>
      </Center>
    );
  }

  return (
    <Stack gap="xs">
      <Group justify="flex-end">
        <Button variant="subtle" color="gray" size="xs" leftSection={<IconTrash size={14} />} onClick={onClear}>
          {t("translation.history.clearAll")}
        </Button>
      </Group>

      <Table.ScrollContainer minWidth={600}>
        <Table verticalSpacing="sm" horizontalSpacing="lg" highlightOnHover layout="fixed" w="100%">
          <Table.Thead>
            <Table.Tr>
              <Table.Th w="12%">{t("translation.history.when")}</Table.Th>
              <Table.Th w="13%">{t("translation.source")}</Table.Th>
              <Table.Th w="22%">{t("translation.targets")}</Table.Th>
              <Table.Th w="29%">{t("translation.history.preview")}</Table.Th>
              <Table.Th w="12%">{t("translation.cost")}</Table.Th>
              <Table.Th w="12%" />
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {pageItems.map((entry) => (
              <Table.Tr key={entry.id}>
                <Table.Td style={{ whiteSpace: "nowrap" }}>
                  <Text size="xs" c="dimmed">
                    {dayjs(entry.createdAt).format("DD MMM, HH:mm")}
                  </Text>
                </Table.Td>
                <Table.Td>
                  <Badge size="xs" variant="light" color="brand" style={{ maxWidth: "none" }}>
                    {languageLabel(entry.sourceLang)}
                  </Badge>
                </Table.Td>
                <Table.Td>
                  <TargetBadges langs={entry.targetLangs} />
                </Table.Td>
                <Table.Td maw={300}>
                  <Anchor component="button" size="sm" lineClamp={1} onClick={() => onOpen(entry)}>
                    {entry.preview || entry.id}
                  </Anchor>
                </Table.Td>
                <Table.Td>
                  <CostCell jobId={entry.id} />
                </Table.Td>
                <Table.Td>
                  <Group gap={4} justify="flex-end" wrap="nowrap">
                    <Tooltip label={t("translation.history.open")}>
                      <ActionIcon variant="subtle" size="sm" onClick={() => onOpen(entry)} aria-label={t("translation.history.open")}>
                        <IconExternalLink size={14} />
                      </ActionIcon>
                    </Tooltip>
                    <Tooltip label={t("common.delete")}>
                      <ActionIcon variant="subtle" color="gray" size="sm" onClick={() => onRemove(entry.id)} aria-label={t("common.delete")}>
                        <IconTrash size={14} />
                      </ActionIcon>
                    </Tooltip>
                  </Group>
                </Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      </Table.ScrollContainer>

      {totalPages > 1 && (
        <Group justify="center" mt="xs">
          <Pagination
            total={totalPages}
            value={page}
            onChange={setPage}
            size="sm"
            radius="md"
            color="brand"
          />
        </Group>
      )}
    </Stack>
  );
};
